import { isValidAutomergeUrl, } from "@automerge/automerge-repo/slim";
import { isKeyhiveDoc } from "./keyhive.js";
/**
 * Find-or-create the account document for a site.
 *
 * The site is responsible for remembering which account doc to use. On a fresh
 * install the document is created here, then passed to `createAccount` before
 * it is stored or returned.
 */
export async function resolveAccountHandle(repo, options) {
    const storage = options.storage ?? globalThis.localStorage;
    const stored = storage.getItem(options.storageKey);
    if (stored && isValidAutomergeUrl(stored)) {
        // A valid stored pointer is the user's real account doc. Never fall
        // through to creating a fresh account here: a transient find failure
        // (sync layer still booting, doc briefly unavailable) would overwrite the
        // pointer and silently orphan the user's whole workspace. Retry briefly,
        // then propagate so boot fails visibly with the pointer intact.
        let lastError;
        for (const delay of [0, 500, 2_000]) {
            if (delay)
                await new Promise((resolve) => setTimeout(resolve, delay));
            try {
                return await repo.find(stored);
            }
            catch (error) {
                lastError = error;
                console.warn(`resolveAccountHandle: could not open stored account ${stored}; retrying`, error);
            }
        }
        throw new Error(`resolveAccountHandle: could not open the stored account ${stored}. ` +
            `Refusing to replace the stored account pointer; reload to retry ` +
            `(or clear localStorage["${options.storageKey}"] to start a fresh account).`, { cause: lastError });
    }
    const handle = await createAccountDocument(repo, options.hive, options.createAccount);
    storage.setItem(options.storageKey, handle.url);
    return handle;
}
async function createAccountDocument(repo, hive, createAccount) {
    const handle = await repo.create2();
    if (hive && isKeyhiveDoc(handle.url)) {
        await hive.addSyncServerRelayToDoc(handle.url);
    }
    handle.change((doc) => {
        doc["@patchwork"] = { type: "account" };
    });
    await createAccount?.(handle, repo);
    return handle;
}
